import {
	ReactElement,
	ReadonlyRefObject,
	Ref,
	useCallback,
	useImperativeHandle,
	useMemo,
	useRef,
	useState
} from "react";
import { CalendarCell } from "./CalendarCell";
import styles from "./YearView.module.less";
import { getShortMonthNames } from "client/DateAndTime/culture";
import {
	addMonths,
	compareDates,
	isBetween,
	monthsInAYear
} from "client/DateAndTime/dateUtils";
import { DateOnly } from "client/DateAndTime/dateOnly";
import {
	generateTrackingId,
	isNil,
	isNotNil
} from "client/Misc/utils";
import { KeyEventArguments } from "../TextInput/TextInput";
import { Key } from "client/Misc/commonTypes";
import { CalendarNavigationKey, isCalendarNavigationKey } from "./Calendar";
import { NormalizedMap } from "client_legacy/ndaw/normalizedMap";

export type YearViewHandle = {
	focusDate(date: DateOnly): void;
};

export type YearViewProps = {
	year: number;
	today: DateOnly;
	highlightedDate: DateOnly | null;
	onSelect(month: number): void;
	onViewRangeChange: (modifier: 1 | -1) => void;
	minDate: DateOnly | null;
	maxDate: DateOnly | null;
	ref: Ref<YearViewHandle>;
};

export const YearView = (props: YearViewProps): ReactElement => {
	const {
		year,
		today,
		highlightedDate,
		onSelect,
		onViewRangeChange,
		minDate,
		maxDate,
		ref
	} = props;

	const [hoverIndex, setHoverIndex] = useState<number | null>(null);
	const [focusableMonth, setFocusableMonth] = useState<DateOnly | undefined>(undefined);

	const itemRefs: ReadonlyRefObject<
		NormalizedMap<DateOnly, HTMLElement | null>
	> = useRef<NormalizedMap<DateOnly, HTMLElement | null>>(
		new NormalizedMap<DateOnly, HTMLElement | null>(x => x.toISOString())
	);

	const months: readonly Month[] = useMemo(
		(): readonly Month[] => {
			const startOfTheYear = new DateOnly(year, 0, 1);

			return getShortMonthNames()
				.map((x, i): Month => {
					const start: DateOnly = addMonths(startOfTheYear, i);
					const end: DateOnly = addMonths(startOfTheYear, i + 1);

					return {
						start,
						end,
						name: x,
						isSelectable: isMonthSelectable(
							start,
							end,
							minDate,
							maxDate
						)
					};
				});
		},
		[
			year,
			minDate,
			maxDate
		]
	);

	const isDateInView = useCallback(
		(date: DateOnly): boolean => {
			return isBetween(
				date,
				months[0].start,
				months[months.length - 1].end
			);
		},
		[months]
	);

	const computedFocusableMonth = useMemo(
		(): DateOnly => {
			if (isNil(focusableMonth) || !isDateInView(focusableMonth)) {
				return months[0].start;
			}

			return focusableMonth;
		},
		[
			focusableMonth,
			isDateInView,
			months
		]
	);

	const setItemRef = useCallback(
		(item: HTMLElement | null, date: DateOnly): void => {
			if (isNil(item)) {
				return;
			}

			itemRefs.current
				.set(date, item);
		},
		[]
	);

	const focusItemHandler = useCallback(
		(targetMonth: DateOnly): void => {
			setFocusableMonth(targetMonth);

			// The setTimeout is used to delay focusing the target month until after the view range has changed
			setTimeout(
				(): void => {
					itemRefs.current
						?.get(targetMonth)
						?.focus();
				},
				1
			);
		},
		[]
	);

	const movementHandler = (args: KeyEventArguments, selectedMonth: DateOnly): boolean => {
		if (isNil(args.key) || !isCalendarNavigationKey(args.key)) {
			return false;
		}

		const keyToOffset: Record<CalendarNavigationKey, number> = {
			[Key.ArrowDown]: YEAR_VIEW_COLUMN_AMOUNT,
			[Key.ArrowUp]: -YEAR_VIEW_COLUMN_AMOUNT,
			[Key.ArrowLeft]: -1,
			[Key.ArrowRight]: 1
		};

		const offset: number = keyToOffset[args.key];
		const targetMonth: DateOnly = addMonths(selectedMonth, offset);

		if (!isDateInView(targetMonth)) {
			onViewRangeChange(offset > 0 ? 1 : -1);
		}

		focusItemHandler(targetMonth);

		return true;
	};

	useImperativeHandle(
		ref,
		(): YearViewHandle => ({
			focusDate: (date: DateOnly): void => {
				focusItemHandler(date);
			}
		}),
		[focusItemHandler]
	);

	return (
		<div
			className={styles.root}
			style={{
				"--column-amount": YEAR_VIEW_COLUMN_AMOUNT
			}}
			role="grid"
		>
			{Array.from({ length: YEAR_VIEW_ROW_AMOUNT }).map((_, rowIndex) => {
				const startIndex = rowIndex * YEAR_VIEW_COLUMN_AMOUNT;
				const cells = months.slice(startIndex, startIndex + YEAR_VIEW_COLUMN_AMOUNT);

				return (
					<div
						key={rowIndex}
						className={styles.row}
						role="row"
					>
						{cells.map((x, i) => {
							const itemIndex = startIndex + i;

							return (
								<CalendarCell
									trackingId={generateTrackingId("month", `|${x.start.monthIndex}`)}
									kind="month"
									key={itemIndex}
									isActive={isNotNil(highlightedDate) && isBetween(
										highlightedDate,
										x.start,
										x.end
									)}
									isToday={isBetween(
										today,
										x.start,
										x.end
									)}
									isDisabled={!x.isSelectable}
									onClick={() => onSelect(x.start.monthIndex)}
									isHovered={hoverIndex === itemIndex}
									onMouseEnter={() => setHoverIndex(itemIndex)}
									onMouseLeave={() => setHoverIndex(null)}
									tabIndex={x.start
										.isEqualTo(computedFocusableMonth)
											? 0
											: -1
									}
									ref={(el) => setItemRef(el, x.start)}
									onKeyDown={args => movementHandler(args, x.start)}
								>
									{x.name}
								</CalendarCell>
							);
						})}
					</div>
				);
			})}
		</div>
	);
};
YearView.displayName = "YearView";

type Month = {
	name: string;
	start: DateOnly;
	end: DateOnly;
	isSelectable: boolean;
};

const YEAR_VIEW_COLUMN_AMOUNT = 4;
const YEAR_VIEW_ROW_AMOUNT: number = monthsInAYear / YEAR_VIEW_COLUMN_AMOUNT;

function isMonthSelectable(
	start: DateOnly,
	end: DateOnly,
	min: DateOnly | null,
	max: DateOnly | null
): boolean {
	const isAfterMin: boolean = isNil(min) || compareDates(min, end) === -1;
	const isBeforeMax: boolean = isNil(max) || compareDates(max, start) >= 0;

	return isAfterMin && isBeforeMax;
}
